/*
 * Cart Upsell ("Often Purchased With") interactions.
 *
 * Works in two contexts (set via data-context on [data-cart-upsell]):
 *   - 'drawer' : add/remove re-renders the slide cart via CartDrawer.renderContents.
 *   - 'page'   : add/remove refreshes the cart page via the theme's cartUpdate event.
 *
 * Control styles (data-control / data-behaviour):
 *   - button                     : [data-upsell-add]         → add 1 of this variant.
 *   - radio + add_instant        : [data-upsell-toggle]      → tick adds, untick removes.
 *   - radio + select_add         : [data-upsell-select] + [data-upsell-add-selected]
 *                                  → tick to select, one button adds all selected.
 *
 * Uses event delegation so it survives drawer re-renders without re-binding.
 */
(function () {
  var ACC_CONTENT = '#often_purchase_product';
  var ACC_TRIGGER = '[data-target="#often_purchase_product"]';

  function drawerEl() {
    return document.querySelector('cart-drawer');
  }

  function addUrl() {
    return (window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js';
  }

  function changeUrl() {
    return (window.routes && window.routes.cart_change_url ? window.routes.cart_change_url : '/cart/change') + '.js';
  }

  function cartUrl() {
    return (window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js';
  }

  function sectionsFor(el) {
    if (el && typeof el.getSectionsToRender === 'function') {
      return el.getSectionsToRender().map(function (s) { return s.id; });
    }
    return null;
  }

  // Whether the drawer upsell accordion is currently expanded.
  function upsellIsOpen() {
    var content = document.querySelector(ACC_CONTENT);
    return !!(content && content.classList.contains('is-active'));
  }

  // After a drawer re-render, rebind the accordion and restore the open state.
  function restoreDrawer(wasOpen) {
    var content = document.querySelector(ACC_CONTENT);
    var trigger = document.querySelector(ACC_TRIGGER);

    if (content && trigger && wasOpen && !content.classList.contains('is-active')) {
      content.classList.add('is-active');
      trigger.classList.add('is-active');
      content.style.height = 'auto';
    }

    if (typeof window.initAccordion === 'function') window.initAccordion();
  }

  function publishUpdate(state) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsell', cartData: state });
    }
  }

  // Refresh the cart after a successful mutation.
  function refresh(state, context, wasOpen) {
    if (context === 'drawer') {
      var drawer = drawerEl();
      // renderContents needs the rendered sections in the payload; if they are
      // missing (e.g. a plain /cart.js response) fall back to the pub/sub path,
      // which re-fetches the drawer contents on its own.
      if (drawer && typeof drawer.renderContents === 'function' && state && state.sections && state.sections['cart-drawer']) {
        drawer.classList.remove('is-empty');
        drawer.renderContents(state);
        restoreDrawer(wasOpen);
        return;
      }
    }
    // Cart page (or no rendered sections): let the theme refresh the line items.
    publishUpdate(state);
  }

  function postJSON(url, payload, context) {
    if (context === 'drawer') {
      var drawer = drawerEl();
      var ids = sectionsFor(drawer);
      if (ids) {
        payload.sections = ids;
        payload.sections_url = window.location.pathname;
        if (typeof drawer.setActiveElement === 'function') drawer.setActiveElement(document.activeElement);
      }
    }
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (r) { return r.json(); });
  }

  function addItems(items, context, wasOpen) {
    return postJSON(addUrl(), { items: items }, context).then(function (state) {
      if (state && state.status) throw state; // Shopify error payload
      refresh(state, context, wasOpen);
      return state;
    });
  }

  // Remove every line matching this variant. Lines carrying properties (e.g. a
  // sample flag) have a "<variant>:<hash>" key and can only be changed by key,
  // not by bare variant id — so look the keys up from the live cart first.
  function removeVariant(variantId, context, wasOpen) {
    return fetch(cartUrl(), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var keys = (cart.items || [])
          .filter(function (i) { return i.variant_id === variantId; })
          .map(function (i) { return i.key; });

        if (!keys.length) {
          // Already gone — just refresh the UI from the current cart.
          refresh(cart, context, wasOpen);
          return cart;
        }

        var chain = Promise.resolve(null);
        keys.forEach(function (key, idx) {
          var last = idx === keys.length - 1;
          chain = chain.then(function () {
            return postJSON(changeUrl(), { id: key, quantity: 0 }, last ? context : null).then(function (state) {
              if (state && state.status) throw state;
              return state;
            });
          });
        });

        return chain.then(function (state) {
          refresh(state, context, wasOpen);
          return state;
        });
      });
  }

  function contextOf(node) {
    var wrap = node.closest('[data-cart-upsell]');
    return wrap ? (wrap.dataset.context || 'drawer') : 'drawer';
  }

  // --- Standard "add" button -------------------------------------------------
  function handleAdd(btn) {
    var id = Number(btn.dataset.variantId);
    if (!id) return;

    var context = contextOf(btn);
    var wasOpen = upsellIsOpen();
    var original = btn.innerHTML;

    btn.setAttribute('aria-busy', 'true');
    btn.style.pointerEvents = 'none';
    btn.style.opacity = '0.6';

    addItems([{ id: id, quantity: 1 }], context, wasOpen).catch(function () {
      // On the cart page the button survives; restore it. In the drawer it is
      // replaced by the re-render, so this is a no-op there.
      btn.innerHTML = original;
      btn.removeAttribute('aria-busy');
      btn.style.pointerEvents = '';
      btn.style.opacity = '';
    });
  }

  // --- Radio: add on tick, remove on untick ----------------------------------
  function handleToggle(input) {
    var id = Number(input.dataset.variantId);
    if (!id) return;

    var context = contextOf(input);
    var wasOpen = upsellIsOpen();
    var checked = input.checked;

    input.disabled = true;

    var op = checked
      ? addItems([{ id: id, quantity: 1 }], context, wasOpen)
      : removeVariant(id, context, wasOpen);

    op.catch(function () {
      // Revert the visual state if the cart mutation failed.
      input.checked = !checked;
      input.disabled = false;
    });
  }

  // --- Radio: select many, add with one button -------------------------------
  function updateAddSelected(wrap) {
    if (!wrap) return;
    var btn = wrap.querySelector('[data-upsell-add-selected]');
    if (!btn) return;
    var any = wrap.querySelectorAll('[data-upsell-select]:checked').length > 0;
    btn.disabled = !any;
  }

  function handleAddSelected(btn) {
    var wrap = btn.closest('[data-cart-upsell]');
    if (!wrap) return;

    var checked = Array.prototype.slice.call(wrap.querySelectorAll('[data-upsell-select]:checked'));
    if (!checked.length) return;

    var items = checked.map(function (cb) { return { id: Number(cb.dataset.variantId), quantity: 1 }; });
    var context = wrap.dataset.context || 'drawer';
    var wasOpen = upsellIsOpen();
    var original = btn.innerHTML;

    btn.disabled = true;
    btn.innerHTML = 'Adding…';

    addItems(items, context, wasOpen).catch(function () {
      btn.disabled = false;
      btn.innerHTML = original;
    });
  }

  // --- Delegated listeners ---------------------------------------------------
  document.addEventListener('click', function (e) {
    var addSelected = e.target.closest('[data-upsell-add-selected]');
    if (addSelected) { e.preventDefault(); handleAddSelected(addSelected); return; }

    var addBtn = e.target.closest('[data-upsell-add]');
    if (addBtn) { e.preventDefault(); handleAdd(addBtn); return; }
  });

  document.addEventListener('change', function (e) {
    var toggle = e.target.closest('[data-upsell-toggle]');
    if (toggle) { handleToggle(toggle); return; }

    var select = e.target.closest('[data-upsell-select]');
    if (select) { updateAddSelected(select.closest('[data-cart-upsell]')); return; }
  });
})();
